import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { MatMenu } from '@angular/material/menu';
import { Router } from '@angular/router';
import { Company } from '@app/models/user.model';
import { ClosedownMode } from '@app/models/closedown-detail.model';
import { AuthService } from '@app/services/auth/auth.service';
import { ClosedownService } from '@app/services/closedown/closedown.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-workbench-header',
  templateUrl: './workbench-header.component.html',
  styleUrls: ['./workbench-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class WorkbenchHeaderComponent implements OnInit {
  readonly jobsMenu = input<MatMenu>();
  readonly invoicesMenu = input<MatMenu>();
  readonly mode = input<ClosedownMode>('admin');
  readonly resourceCompanyId = input<string | null>(null);
  readonly isRedirectingToWorkbench = signal(false);
  readonly isMobileNavOpen = signal(false);
  readonly companies = signal<Company[]>([]);
  readonly visibleCompanies = computed(() => this.mode() === 'technician'
    ? this.companies().filter(company => company.id === this.resourceCompanyId())
    : this.companies());
  readonly selectedCompany = computed<Company | null>(() => this.visibleCompanies()[0] ?? null);
  readonly companyLoadError = signal(false);

  private readonly authService = inject(AuthService);
  private readonly closedownService = inject(ClosedownService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);

  ngOnInit(): void {
    this.authService.getUser().then(
      user => {
        this.companies.set((user?.companies ?? []).filter(company => !!company.id));
      },
      () => {
        this.companyLoadError.set(true);
      }
    );
  }

  toggleMobileNav(): void {
    this.isMobileNavOpen.update(isOpen => !isOpen);
  }

  logout(): void {
    if (confirm(this.translateService.instant('appcomponent_dialog_logout'))) {
      void this.router.navigate(['/logout']);
    }
  }

  async openWorkbench(): Promise<void> {
    if (this.isRedirectingToWorkbench()) {
      return;
    }

    this.isRedirectingToWorkbench.set(true);
    try {
      await this.closedownService.directToClosedown(false);
    } finally {
      this.isRedirectingToWorkbench.set(false);
    }
  }
}
